import React, { useState } from "react";
import { FaTrash } from "react-icons/fa";
import Button from "../../common/Button/Button";
import { deletePlanCountryImageAssociation, deletePlanImage } from "../../service/planCountryImageService";
import AddImageModal from "./AddImageModal";

const MainTabContent = ({
  images,
  plans,
  eventTypeId,
  get,
  post,
  showToast,
  onDataRefresh,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);

  const handleDeleteTag = async (planCountryImageId) => {
    if (!eventTypeId) {
      showToast("Event Type ID not found.", "error");
      return;
    }

    setDeletingTagId(planCountryImageId);
    try {
      const result = await deletePlanCountryImageAssociation(
        post,
        eventTypeId,
        planCountryImageId
      );

      if (result.success) {
        showToast("Tag deleted successfully");
        onDataRefresh();
      } else {
        showToast(result.message, "error");
      }
    } catch (error) {
      showToast("Failed to delete tag. Please try again.", "error");
    } finally {
      setDeletingTagId(null);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!eventTypeId) {
      showToast("Event Type ID not found.", "error");
      return;
    }

    // Confirm deletion
    if (!window.confirm("Are you sure you want to delete this image? This action cannot be undone.")) {
      return;
    }

    setDeletingImageId(imageId);
    try {
      const result = await deletePlanImage(post, eventTypeId, imageId);

      if (result.success) {
        showToast("Image deleted successfully");
        onDataRefresh();
      } else {
        showToast(result.message, "error");
      }
    } catch (error) {
      showToast("Failed to delete image. Please try again.", "error");
    } finally {
      setDeletingImageId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Images</h2>
        <Button
          text="Add Image"
          className="bg-orange-primary text-white px-4 py-2 rounded"
          onClick={() => setIsAddModalOpen(true)}
        />
      </div>

      {images.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No images found. Click "Add Image" to upload your first image.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((item) => {
            const image = item.image;
            const planDetails = item.plan_details || [];

            return (
              <div
                key={image.id}
                className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 relative"
              >
                {/* Delete Icon - Top Right */}
                <button
                  onClick={() => handleDeleteImage(image.id)}
                  disabled={deletingImageId === image.id}
                  className="absolute top-2 right-2 z-10 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Delete image"
                >
                  <FaTrash className="w-4 h-4" />
                </button>

                {/* Image Container - 16:9 Aspect Ratio */}
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <img
                    src={image.img_sas_url || image.img_url}
                    alt={image.img_name || "Plan image"}
                    className="absolute top-0 left-0 w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = image.img_url; // Fallback to regular URL
                    }}
                  />
                </div>

                {/* Image Info */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">
                    {image.img_name || "Untitled Image"}
                  </h3>

                  {/* Plan Tags */}
                  {planDetails.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {planDetails.map((planDetail) => (
                        <span
                          key={planDetail.id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium"
                        >
                          {planDetail.plan_name || "no plan"}
                          <button
                            onClick={() => handleDeleteTag(planDetail.id)}
                            disabled={deletingTagId === planDetail.id}
                            className="ml-1 text-orange-600 hover:text-orange-800 disabled:opacity-50"
                            title="Delete tag"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                      no plan
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAddModalOpen && (
        <AddImageModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          plans={plans}
          eventTypeId={eventTypeId}
          get={get}
          post={post}
          showToast={showToast}
          onSuccess={() => {
            setIsAddModalOpen(false);
            onDataRefresh();
          }}
        />
      )}
    </div>
  );
};

export default MainTabContent;

